import React, { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button, useIsMobile, useToast } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import { CYCLE_PRICE_NOTES } from "../data/subscriptionPlans";
import PinnedCtaBar from "./PinnedCtaBar";
import { monthsLabel, planTermLabel } from "../pricing";
import type { BillingCycleId, SubscriptionPlan } from "../types/billing.types";

/**
 * What the list can be set to: one of the terms on sale, or — where the screen
 * offers one — the free trial standing at the head of them.
 *
 * The trial is a choice *in* this list rather than a card above it, because it
 * answers the same question the terms do: how do you want to start. Two
 * controls asking that is how somebody ends up pressing one and getting the
 * other.
 */
export type DurationChoice = BillingCycleId | "trial";

/** The free-trial row, for the screen that offers one. */
export interface TrialOption {
  /** "Activate 14 day trial". */
  title: string;
  /** The line under it — what it does and does not ask for. */
  note: string;
  /** What it costs, in a word: "Free". */
  price: string;
  /** What the button says while this row is the pick. */
  cta: string;
  /** Taking it. */
  onStart: () => void;
}

interface PlanDurationsProps {
  /** What is on sale, shortest term first — `plansService` has already put
   *  them in order, and this draws one row per plan rather than per term it
   *  expects to exist. */
  plans: SubscriptionPlan[];
  /** The choice made, or `null` for a list nothing has been picked from yet. */
  value: DurationChoice | null;
  onChange: (choice: DurationChoice) => void;
  /** Buying the term that is picked. The list decides nothing itself. */
  onBuy: (plan: SubscriptionPlan) => void;
  /**
   * The free trial, where this screen has one to offer — drawn as the first
   * row of the list. Left off everywhere else, which is every screen a
   * workspace reaches once its trial is spent.
   */
  trial?: TrialOption | null;
  /** The term the workspace is already running on, which cannot be bought
   *  again — whose it is and what counts as running is the screen's to decide;
   *  see `BillingSection`. */
  activeCycle?: BillingCycleId | null;
  /** The purchase is on its way — the button spins. */
  busy?: boolean;
  /**
   * On a phone, the button leaves the column and takes the bottom edge of the
   * screen — see `PinnedCtaBar`.
   *
   * Asked for by the screen rather than assumed. The bar is full-bleed and it
   * stands the app's tab bar down, so it is only right on a screen that is a
   * whole page; a list drawn inside a card or a dialog would be pinning a bar
   * across something that is not the screen. Both places this list appears on
   * a phone are whole pages, and both ask for it.
   */
  pinCta?: boolean;
  /**
   * The gutter the screen around this list keeps, kept inside the bar.
   *
   * The bar is the width of the window and sits on its bottom edge, rather
   * than being written into this column — see `PinnedCtaBar`. So the blur runs
   * edge to edge while the button inside it keeps the page's own margin and
   * still lines up with the rows above it, and the only thing the shell has to
   * say is what that margin is. It used to have to say it three times — the
   * gutter cancelled, spent again, and the page's last padding taken off — and
   * a bar held against the window has nothing left to cancel.
   *
   * The default is the page gutter every screen keeps, which is the Settings
   * page's; the trial offer's shell widens it from `sm` up and passes its own.
   *
   * Classes rather than a flag, because there is no third thing this could
   * be: either the caller names its own gutter or it gets a button that does
   * not line up with the page it is on.
   */
  ctaGutter?: string;
}

/**
 * The dot that says which row is picked, drawn on a phone only.
 *
 * On a wide screen the row says it three ways at once — the teal border, the
 * tint, and the price grown a few points — and a fourth would be belt and
 * braces. On a phone the design puts the control back, and it is right to:
 * the rows run the full width of the screen there, so there is no second row
 * beside one to read the difference against.
 *
 * `aria-hidden`, because the button around it is already a `radio` carrying
 * `aria-checked`. This is the picture of a state that is announced without
 * it.
 */
const Dot: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    aria-hidden="true"
    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors md:hidden ${
      checked ? "border-[#00C2CB]" : "border-[#CBD5DA]"
    }`}
  >
    {checked && <span className="h-2.5 w-2.5 rounded-full bg-[#00C2CB]" />}
  </span>
);

/**
 * How long to commit for: one row per term on sale, and one button under them.
 *
 * ## It draws the price list, not a design
 *
 * Four rows were drawn for this — a month, a quarter, a half-year and a year —
 * and the list renders whatever `GET /billing/plans/` actually prices. Today
 * that is two of the four. Nothing here invents a row for a term the backend
 * does not sell, and nothing has to change when it starts selling one: see
 * `CYCLES` in `plansService`, which is the whole of what a new term costs.
 *
 * ## One question, one answer
 *
 * A radio group rather than a row of buttons: this is one question — for how
 * long — and exactly one answer can hold, which is what the arrow keys and the
 * announced "2 of 4" both depend on. `role="radio"` on a `<button>` keeps the
 * row's own hover and focus ring, which a bare `<input>` would not give without
 * being hidden and redrawn.
 *
 * ## The button names what it buys
 *
 * Nothing picked, nothing to buy: it reads "Select plan" and is off, because a
 * button that says "Buy" before anything has been chosen is a button that
 * cannot say what for. Pick a term and it becomes "Buy 3 month plan" — the term
 * on the thing that buys it, rather than only in a row four inches above.
 *
 * ## The trial is a row, not a card above the rows
 *
 * On the screen a new account lands on, the fortnight is offered as the first
 * row of this same list — see `TrialOption`. It belongs there because it is an
 * answer to the question the list is asking: how do you want to start. Offered
 * as a card above instead, it would be a second control asking the same thing,
 * and the button underneath could only name one of them.
 */
const PlanDurations: React.FC<PlanDurationsProps> = ({
  plans,
  value,
  onChange,
  onBuy,
  trial = null,
  activeCycle = null,
  busy = false,
  pinCta = false,
  ctaGutter = "px-page",
}) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  // Asked of the browser rather than of a `md:` class, because the bar is a
  // mount and not a style: it is what hides the app's tab bar, and a bar that
  // was only hidden by CSS would still be holding the foot of the screen.
  const pinned = pinCta && isMobile;
  // Which code has just been copied, so the row that was pressed confirms it
  // rather than the page announcing it somewhere else.
  const [copied, setCopied] = useState<string | null>(null);

  const onTrial = value === "trial";
  const picked = plans.find((plan) => plan.id === value) ?? null;
  /**
   * Whether this is a move off a term that is running rather than a first
   * purchase, and whether that move waits.
   *
   * They decide what the button says and nothing else. Deliberately "switch"
   * and not "upgrade": whether the backend credits the days left on the term
   * being replaced is its judgement on the quote the next screen opens with,
   * and this list has no quote. What it can see is two lengths, and the
   * product rule over them is fixed — a longer term starts today and a shorter
   * one is queued behind the one running — so "at term end" is said where it
   * is true and the checkout corrects the rest.
   *
   * Said at all because it is the question somebody picking a shorter plan is
   * actually asking: a button reading only "Switch to 3 months" over a term
   * with months left on it reads as an offer to cut it short today.
   */
  const held = plans.find((plan) => plan.id === activeCycle) ?? null;
  const switching = !!held && !!picked && picked.id !== held.id;
  const laterStart = switching && picked!.months < held!.months;

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // A browser that refuses the clipboard — an insecure origin, a denied
      // permission — still leaves the code on screen to be read off. Said out
      // loud so nobody presses it twice believing it worked.
      toast.info(
        "Copy the code by hand",
        `Your browser would not let us copy it. The code is ${code}.`,
      );
    }
  };

  /**
   * The one button under the list, which names what it is about to buy.
   *
   * Built here rather than inline because it is drawn in one of two places —
   * under the last row on a wide screen, inside the pinned bar on a phone —
   * and the button itself is the same button in both.
   */
  const cta = (
    <Button
      variant="primary"
      size="lg"
      fullWidth
      loading={busy}
      disabled={!onTrial && (!picked || picked.id === activeCycle)}
      // A disabled button that has a reason should give it, rather than
      // leaving somebody pressing a dead control and guessing.
      onDisabledClick={
        picked && picked.id === activeCycle
          ? () =>
              toast.info(
                "You are already on this plan",
                "Pick a different duration to change what you are paying for.",
              )
          : () =>
              toast.info(
                "Pick a duration first",
                "Choose how long you want to subscribe for, then press buy.",
              )
      }
      onClick={() => {
        if (onTrial) {
          trial?.onStart();
          return;
        }
        if (picked) onBuy(picked);
      }}
      // The gap the pinned bar keeps is its own padding, so the button inside
      // it carries none of the column's.
      className={pinned ? undefined : "mt-1"}
    >
      {/* The button names what it is about to do, which is a different thing
          on the trial row than on a term. */}
      {onTrial && trial
        ? trial.cta
        : picked
          ? switching
            ? `Switch to ${monthsLabel(picked.months)}${
                laterStart ? " at term end" : ""
              }`
            : `Buy ${planTermLabel(picked.months)}`
          : "Select plan"}
    </Button>
  );

  return (
    // `h-full` so this column fills the row the panel beside it sets, and the
    // two end on the same line. The rows stay at the top; the button below
    // them is pushed to the foot rather than sitting directly under the last
    // row, so the space the taller column creates falls between them instead
    // of trailing off the bottom.
    <div className="flex h-full flex-col gap-3 3xl:gap-4">
      {/* `flex-1` so the rows share whatever height is left over once the
          panel beside them has set the column's. They grow; the space does
          not pool between the last row and the button under it. */}
      <div
        role="radiogroup"
        aria-label="Subscription duration"
        className="flex flex-1 flex-col gap-3 3xl:gap-4"
      >
        {trial && (
          // First, and the one the page opens on: somebody who has not signed
          // up before is being offered a fortnight, and the terms under it are
          // what it costs once the fortnight is over. Same row, same control,
          // so the whole question is answered in one place.
          <button
            type="button"
            role="radio"
            aria-checked={onTrial}
            disabled={busy}
            onClick={() => onChange("trial")}
            // A row rather than a column, so the dot stands beside the
            // title and the note under it rather than above both. From `md`
            // up the dot is not drawn at all, and a `display:none` child is
            // not a flex item — the gap goes with it, and the row is exactly
            // the row it was.
            className={`flex w-full flex-1 items-center gap-3 rounded-md border px-4 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 disabled:cursor-not-allowed sm:px-5 3xl:px-6 3xl:py-5 ${
              onTrial
                ? "border-[#00C2CB] bg-[#F4FDFE]"
                : "border-line bg-white hover:border-[#9FE3EA]"
            }`}
          >
            <Dot checked={onTrial} />

            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex w-full items-center justify-between gap-3">
                <span className="truncate text-[17px] sm:text-[19px] 3xl:text-[21px] font-semibold leading-none text-ink">
                  {trial.title}
                </span>
                {/* Green rather than the brand teal, and the only green on this
                  list: "Free" is not a price in the column of prices beside it,
                  it is the absence of one, and setting it in the same colour
                  would file it as the cheapest of five figures. */}
                <span className="flex-shrink-0 text-[15px] sm:text-[17px] 3xl:text-[19px] font-semibold leading-none text-[#09BF4F]">
                  {trial.price}
                </span>
              </span>
              {/* Teal rather than the grey every other secondary line on
                  this list takes. It is not a caption under a price — it is
                  the answer to the question the row raises, which is "what
                  is the catch", and the design gives it the brand's own ink
                  for exactly that reason. */}
              <span className="mt-1.5 block text-[11px] leading-[1.5] text-[#007E8C] 3xl:text-[12px]">
                {trial.note}
              </span>
            </span>
          </button>
        )}

        {plans.map((plan) => {
          const selected = plan.id === value;
          const active = plan.id === activeCycle;
          const note = CYCLE_PRICE_NOTES[plan.id];
          const saving = plan.listCyclePrice - plan.cyclePrice;

          return (
            <div key={plan.id} className="relative flex flex-1 flex-col">
              {/* Sat on the row's top edge rather than inside it: the row is a
                  hairline box and a badge within it would push the price off
                  its own line on a narrow column. */}
              {plan.recommended && (
                <span className="absolute -top-2 left-4 z-10 rounded-full bg-[#09BF4F] px-2 py-0.5 text-[9px] 3xl:text-[10px] font-bold uppercase tracking-wider text-white sm:left-5 3xl:left-6">
                  Most subscribed
                </span>
              )}

              <button
                type="button"
                role="radio"
                aria-checked={selected}
                // Named in full, because the visible row is a term and two
                // figures: read out alone it would be "3 months, ₹1800, ₹2400"
                // with no word for which of the two is being charged.
                aria-label={`${plan.cycleLabel}, ${formatPrice(
                  plan.cyclePrice,
                )}${
                  saving > 0
                    ? `, normally ${formatPrice(plan.listCyclePrice)}`
                    : ""
                }${active ? ", your current plan" : ""}`}
                // The term already running is not a choice. It used to be
                // pickable and simply bar the button underneath, which asked
                // somebody to press a row, read a button and then a toast to
                // find out that the row did nothing — three steps to learn
                // what the row itself can say. It now says it: greyed, out of
                // use, and wearing what it is.
                disabled={busy || active}
                onClick={() => onChange(plan.id)}
                // `flex-1` to fill the row's share of the leftover height,
                // and `justify-center` so the term and its price sit in the
                // middle of whatever height that turns out to be rather than
                // riding the top edge of a box that grew under them.
                className={`flex w-full flex-1 flex-col justify-center gap-1 rounded-md border px-4 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 disabled:cursor-not-allowed sm:px-5 3xl:px-6 3xl:py-5 ${
                  active
                    ? "border-line bg-[#F7F9FA]"
                    : selected
                      ? "border-[#00C2CB] bg-[#F4FDFE]"
                      : "border-line bg-white hover:border-[#9FE3EA]"
                } ${
                  // Room at the top for the badge that straddles this row's
                  // edge. The badge hangs out of the flow — it has to, to sit
                  // on the border rather than inside it — so without this the
                  // term would be printed underneath it.
                  plan.recommended ? "pt-6 3xl:pt-7" : ""
                } ${
                  // Room at the foot of the row for the note that sits over it.
                  // Reserved here rather than left to the note's own height:
                  // the note is positioned out of the flow — it has to be, so
                  // its copy button is not nested inside this one — and a row
                  // that did not make space would have it printed across the
                  // price.
                  note ? "pb-11 3xl:pb-12" : ""
                }`}
              >
                {/* The line is pinned to one height, and that is what lets the
                    figure grow on the row that is picked without the row
                    growing with it.

                    Every piece of text on it is set `leading-none`, so each
                    one is exactly as tall as its own font and nothing carries
                    invisible half-leading; the `min-h` is then set to clear the
                    largest of them — the selected price — in every state. The
                    values are what the line already stood at, so a row is no
                    taller than it was before any of this, and selecting one
                    moves nothing on the page. */}
                <span className="flex min-h-[26px] w-full items-center gap-3 sm:min-h-[29px] 3xl:min-h-[32px]">
                  {/* No dot on the row that is already running: a radio
                      beside a row nothing can pick is the one mark on this
                      list that would be lying. The space it stood in is kept
                      so the four rows still read down one column on a phone,
                      which is the only width the dot is drawn at. */}
                  {active ? (
                    <span
                      aria-hidden="true"
                      className="h-5 w-5 flex-shrink-0 md:hidden"
                    />
                  ) : (
                    <Dot checked={selected} />
                  )}

                  {/* `flex-1` where the line used to be `justify-between`:
                      with the dot in front there are three things on it, and
                      the middle one taking whatever is left is what holds the
                      price against the right edge.

                      A column on a phone, because that is the only width
                      where the badge beside the term is what the line cannot
                      afford: the price holds its own width, so the term and
                      the badge split what is left, and on a phone that is not
                      enough for both — the term lost, and "6 months" was
                      printed as "6". Stacked, the term has the whole of that
                      width to itself and the badge sits under it at its full
                      wording, which is the one place on the row where neither
                      has to give way. From `sm` up there is room for both on
                      one line, and the row is the row it has always been. */}
                  <span className="flex min-w-0 flex-1 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
                    <span
                      className={`w-full truncate text-[17px] sm:w-auto sm:text-[19px] 3xl:text-[21px] font-semibold leading-none ${
                        active ? "text-ink-subtle" : "text-ink"
                      }`}
                    >
                      {plan.cycleLabel}
                    </span>
                    {active && (
                      // Said in full rather than as "Active". On a list whose
                      // whole job is picking the next term, a one-word badge
                      // reads as a state a row could be put into; the two
                      // words say which of the four is running now.
                      <span className="max-w-full flex-shrink-0 rounded-full bg-brand-soft px-2 py-0.5 text-[9px] 3xl:text-[10px] font-bold uppercase tracking-wider text-brand-dark">
                        Currently active
                      </span>
                    )}
                  </span>

                  {/* Baseline-aligned so the struck figure sits on the line the
                      big one stands on rather than floating against its
                      middle. */}
                  <span className="flex flex-shrink-0 items-baseline gap-1.5">
                    {/* Both figures grow on the row that is picked — what it
                        costs and what it is listed at.

                        Size is doing what a tick would otherwise have to: of
                        four rows that differ only in a term and a price, the
                        one being bought is the one whose price is the biggest
                        thing on the list. The tint and the teal border say the
                        same thing, and this is what makes it legible from
                        across the page rather than only on inspection.

                        The struck price comes with it rather than staying put,
                        because the two are one claim: a price that doubled in
                        size beside a "was" figure that did not would read as
                        the pair drifting apart. They keep their ratio, so the
                        discount is as legible on the picked row as on any
                        other. */}
                    <span
                      className={`tabular-nums font-bold leading-none transition-all ${
                        active ? "text-ink-subtle" : "text-ink"
                      } ${
                        selected
                          ? "text-[21px] sm:text-[24px] 3xl:text-[27px]"
                          : "text-[17px] sm:text-[19px] 3xl:text-[21px]"
                      }`}
                    >
                      {formatPrice(plan.cyclePrice)}
                    </span>
                    {saving > 0 && (
                      <s
                        className={`tabular-nums font-medium leading-none text-ink-subtle transition-all ${
                          selected
                            ? "text-[14px] 3xl:text-[16px]"
                            : "text-[12px] 3xl:text-[13px]"
                        }`}
                      >
                        {formatPrice(plan.listCyclePrice)}
                      </s>
                    )}
                  </span>
                </span>
              </button>

              {/* Outside the row's own button, and that is deliberate: the code
                  has a button of its own, and a button inside a button is not
                  something a browser or a screen reader can make sense of.
                  Positioned over the row's lower half so it still reads as part
                  of it. */}
              {note && (
                <p
                  // `pl-8` is the dot plus the gap after it, so the note
                  // starts under the term rather than under the control.
                  // Padding rather than a bigger `left`, which would be two
                  // media queries fighting over one property.
                  className="pointer-events-none absolute bottom-3 left-4 flex items-center gap-1 pl-8 text-[11px] 3xl:text-[12px] text-ink-muted sm:left-5 md:pl-0 3xl:bottom-4 3xl:left-6"
                >
                  {note.note}
                  {note.code && (
                    <>
                      <span className="font-semibold text-[#6234CC]">
                        {note.code}
                      </span>
                      <button
                        type="button"
                        onClick={() => copy(note.code as string)}
                        aria-label={`Copy coupon code ${note.code}`}
                        className="pointer-events-auto -m-1 p-1 text-ink-subtle transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
                      >
                        {copied === note.code ? (
                          <Check
                            aria-hidden
                            className="h-3.5 w-3.5 text-[#00A86B]"
                          />
                        ) : (
                          <Copy aria-hidden className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {/* Said rather than only shown: the tick replacing the
                          icon is invisible to anyone not looking at it. */}
                      <span role="status" className="sr-only">
                        {copied === note.code ? `${note.code} copied` : ""}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* No `mt-auto` here on purpose. The rows above take the leftover
          height themselves, so this sits directly under the last one at the
          column's own gap — pushing it to the foot instead would reopen the
          empty band between the two that growing the rows is here to close.

          On a phone it is lifted out of the column altogether and held across
          the bottom of the screen on the same glass the two checkout steps and
          the confirmation use — one bar for the whole buy flow, so the button
          does not change surface between picking a plan and paying for it. The
          bar is also what stands the app's tab bar down; see `PinnedCtaBar`
          and `pinCta`.

          `floating`, and this is the one screen in the flow that needs it: a
          phone stacks the panel above these rows, so this column does not
          begin until the panel has been scrolled past. A bar stuck to the foot
          of a column that starts below the fold is a bar nobody can see until
          they have scrolled to the thing it was meant to save them scrolling
          to — which is how a screen ends up with no button on it. Held against
          the window instead, it is there from the first paint. See `floats`. */}
      {pinned ? (
        <PinnedCtaBar floating className={ctaGutter}>
          {cta}
        </PinnedCtaBar>
      ) : (
        cta
      )}
    </div>
  );
};

export default PlanDurations;
